<!-- components/Kilometraje/Kilometros.vue -->
<template>
  <!--
    Sección “Kilometraje” (legacy Laravel -> Nuxt/Vue3)
    - Estructura: Título + km destacado + 2 columnas de ítems con icono
    - Alterna fondo gris según el bloque (como en Laravel)
    - Reutiliza ListaAgendadoresLegacy para estandarizar la lista
  -->
  <section class="w-full">
    <div
      v-for="(bloque, index) in bloques_kilometraje"
      :key="bloque.km"
      class="w-full bg-no-repeat bg-cover bg-center"
      :style="estilo_fondo(bloque)"
    >
      <!--
        Paddings replicados del CSS legacy:
        - Desktop base: 50px 160px
        - Mobile <= 600px: 30px 20px
        - 1400–1800: 70px 130px
      -->
      <div
        class="
          py-[50px] px-[160px]
          max-[600px]:py-[30px] max-[600px]:px-[20px]
          min-[1400px]:max-[1800px]:py-[70px]
          min-[1400px]:max-[1800px]:px-[130px]
        "
      >
        <!-- Encabezado -->
        <header class="w-full text-center">
          <!-- En Laravel solo aparece el H2 en el primer bloque -->
          <h2
            v-if="index === 0"
            class="font-bold text-[28px] leading-tight text-GRIS_LEON max-[600px]:text-[20px]"
          >
            {{ titulo_principal }}
          </h2>

          <!-- En Laravel: h3 grande con km rojo -->
          <h3
            class="
              font-bold leading-none
              text-[60px] mt-[40px] mb-[40px]
              max-[600px]:text-[40px]
              text-GRIS_LEON
            "
          >
            <span class="text-ROJO_LEON">{{ bloque.km }}</span>
            {{ texto_kilometros }}
          </h3>
        </header>

        <!--
          Layout como Bootstrap:
          - col-12 col-md-6 + col-12 col-md-6
        -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-y-4 md:gap-y-0 md:gap-x-10">
          <!-- Columna izquierda -->
          <ListaAgendadoresLegacy
            :usarEstilosLegacy="false"
            :mostrarTitulo="false"
            :items="bloque.col_izq"
            listClass="flex flex-col gap-[10px]"
            itemRowClass="flex items-center"
            textClass="m-0 ml-3 text-[15px] leading-snug text-GRIS_LEON max-[600px]:text-[15px]"
            imgLoading="lazy"
            imgDecoding="async"
          >
            <!--
              ROBUSTEZ: evitamos “imagen rota”
              - Si el icono no existe, lo ocultamos y no rompe la UI
            -->
            <template #icono="{ item }">
              <img
                v-if="item.icon_src"
                :src="item.icon_src"
                :alt="item.icon_alt ?? ''"
                class="shrink-0 w-[66px] h-[66px] md:w-[66px] md:h-[66px] object-contain"
                loading="lazy"
                decoding="async"
                @error="on_icon_error"
              />
            </template>
          </ListaAgendadoresLegacy>

          <!-- Columna derecha -->
          <ListaAgendadoresLegacy
            :usarEstilosLegacy="false"
            :mostrarTitulo="false"
            :items="bloque.col_der"
            listClass="flex flex-col gap-[10px]"
            itemRowClass="flex items-center"
            textClass="m-0 ml-3 text-[15px] leading-snug text-GRIS_LEON max-[600px]:text-[15px]"
            imgLoading="lazy"
            imgDecoding="async"
          >
            <template #icono="{ item }">
              <img
                v-if="item.icon_src"
                :src="item.icon_src"
                :alt="item.icon_alt ?? ''"
                class="shrink-0 w-[66px] h-[66px] md:w-[66px] md:h-[66px] object-contain"
                loading="lazy"
                decoding="async"
                @error="on_icon_error"
              />
            </template>
          </ListaAgendadoresLegacy>
        </div>
      </div>
    </div>

    <!-- =========================================================
      ✅ NUEVO (SIN ROMPER LO EXISTENTE):
      Sección “50.000 Kilómetros” (layout tren2 legacy)
      - Desktop: 2 columnas (imagen izquierda alineada a la derecha + texto derecha)
      - Mobile: column-reverse (texto arriba, imagen abajo)
      - Fondo con overlay para legibilidad (como la captura)
      ========================================================= -->
    <div
      class="w-full bg-no-repeat bg-cover bg-center relative"
      :style="estilo_fondo(bloque_50000)"
      aria-label="Sección 50.000 Kilómetros"
    >
      <!-- Overlay oscuro para que el texto sea legible (como la imagen adjunta) -->
      <div class="absolute inset-0 bg-black/55"></div>

      <div
        class="
          relative z-10
          py-[50px] px-[160px]
          max-[600px]:py-[30px] max-[600px]:px-[20px]
        "
      >
        <!-- En mobile debe ser column-reverse: texto arriba, imagen abajo -->
        <div class="flex flex-col-reverse md:flex-row md:gap-x-10 gap-y-6">
          <!-- Imagen -->
          <div class="w-full md:w-1/2 flex justify-center md:justify-end">
            <img
              :src="bloque_50000.img_src"
              :alt="bloque_50000.img_alt"
              class="
                w-full
                md:w-[63%]
                min-[1400px]:max-[1800px]:w-full
                rounded-[18px]
                shadow-2xl
                ring-1 ring-white/15
                transition-transform duration-1000
                hover:scale-110
              "
              loading="lazy"
              decoding="async"
              @error="on_icon_error"
            />
          </div>

          <!-- Texto -->
          <div class="w-full md:w-1/2 flex flex-col justify-center text-white">
            <h2 class="font-bold leading-tight text-[55px] max-[600px]:text-[41px]">
              {{ bloque_50000.titulo }}
            </h2>

            <div class="flex items-center gap-3 mt-4 mb-5 max-[600px]:mb-4">
              <!-- Icono en círculo rojo (como la captura) -->
              <span
                class="
                  inline-flex items-center justify-center
                  shrink-0
                  rounded-full
                  bg-ROJO_LEON
                  w-[42px] h-[42px]
                  max-[600px]:w-[36px] max-[600px]:h-[36px]
                "
                aria-hidden="true"
              >
                <img
                  :src="bloque_50000.icon_src"
                  :alt="bloque_50000.icon_alt"
                  class="
                    w-[22px] h-[22px]
                    max-[600px]:w-[18px] max-[600px]:h-[18px]
                    object-contain
                  "
                  loading="lazy"
                  decoding="async"
                  @error="on_icon_error"
                />
              </span>

              <h3 class="font-semibold leading-snug text-[28px] max-[600px]:text-[21px]">
                {{ bloque_50000.subtitulo }}
              </h3>
            </div>

            <p class="text-[15px] leading-relaxed max-w-[560px]">
              {{ bloque_50000.parrafo_1 }}
            </p>

            <!--
              <p class="text-[15px] leading-relaxed mt-5 max-w-[560px]">
                {{ bloque_50000.parrafo_2 }}
              </p>
            -->

          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/**
 * Componente: Kilometraje (Kilometros.vue)
 * - No depende de window/document (SSR friendly).
 * - Data-driven: si mañana agregas “50.000”, solo sumas un bloque.
 */
defineOptions({ name: 'KilometrajeKilometros' });

import ListaAgendadoresLegacy from '~/components/Comunes/ListaAgendadoresLegacy.vue';

/* =========================
 * Textos principales
 * ========================= */
const titulo_principal = '¿Qué se debe revisar según los kilómetros recorridos?';
const texto_kilometros = 'Kilómetros';

/* =========================
 * Assets (rutas legacy)
 * Nota: estas rutas deben existir en /public/img/kilometraje/*
 * Si falta alguna, el @error la oculta (no rompe el layout).
 * ========================= */
const iconos = Object.freeze({
  aceite: '/img/kilometraje/ico-aceite.png',
  neuma: '/img/kilometraje/ico-neuma.png',
  check: '/img/kilometraje/ico-check.png',
  gota: '/img/kilometraje/ico-gota.png',
  elev: '/img/kilometraje/ico-elev.png',
  amorti: '/img/kilometraje/ico-amorti.png',
  alibal: '/img/kilometraje/ico-alibal.png',
  rot: '/img/kilometraje/ico-rot.png',
  bat: '/img/kilometraje/ico-bat.png',
  disc: '/img/kilometraje/ico-disc.png',
  fren: '/img/kilometraje/ico-fren.png',
  filt: '/img/kilometraje/ico-filt.png',
  acei: '/img/kilometraje/ico-acei.png',
  engra: '/img/kilometraje/ico-engra.png',
  palanca: '/img/kilometraje/ico-palanca.png',
  radiador: '/img/kilometraje/ico-radiador.png',
});

const fondo_gris = '/img/kilometraje/fondo_gris.jpg';

/* =========================
 * ✅ NUEVO: assets sección 50.000
 * (NO afecta lo anterior)
 * Si tus assets están en otra carpeta (ej: /images/landing),
 * cambia SOLO estos strings.
 * ========================= */
const assets_50000 = Object.freeze({
  fondo: '/img/kilometraje/fondo-tablero.jpg',
  imagen: '/img/kilometraje/img1.png',
  icono_repetir: '/img/kilometraje/ico-rept.png',
});

/* =========================
 * Helpers: estructura de item
 * (compatible con PasoServicioItem)
 * ========================= */
const crear_item = (icon_src, texto) => ({
  icon_src,
  icon_alt: '',
  texto,
});

/* =========================
 * Data: bloques (equivalente al HTML Laravel)
 * ========================= */
const bloques_kilometraje = [
  {
    km: '5.000',
    bg_image: '',
    col_izq: [
      crear_item(iconos.aceite, 'Cambio de aceite de motor, filtro de aceite y filtro de aire.'),
      crear_item(
        iconos.neuma,
        'Presión de inflado de los neumáticos y desgaste de la banda de rodamiento (esto se debe realizar en todas las revisiones).'
      ),
      crear_item(iconos.check, 'Tensión del embrague y correas del motor.'),
    ],
    col_der: [
      crear_item(iconos.gota, 'Completar el nivel de líquidos de los diferentes sistemas del auto.'),
      crear_item(iconos.elev, 'Realizar una inspección a diferentes alturas de la carrocería.'),
      crear_item(iconos.amorti, 'Revisar el ajuste de la suspensión.'),
    ],
  },

  {
    km: '10.000',
    bg_image: fondo_gris,
    col_izq: [
      crear_item(
        iconos.aceite,
        'Cambio de aceite de motor, filtro de aceite y filtro de aire. La periodicidad del cambio de aceite depende de las recomendaciones del fabricante.'
      ),
      crear_item(iconos.gota, 'Completar nivel de líquidos: agua de batería, diferencial, hidráulico, etc.'),
      crear_item(
        iconos.alibal,
        'Alineación y balanceo. Se debe realizar cada 10.000 kilómetros o si se ha pasado por un bache o tenido algún choque o topón.'
      ),
      crear_item(iconos.rot, 'Es conveniente, también, realizar rotación de neumáticos.'),
    ],
    col_der: [
      crear_item(
        iconos.bat,
        'Verificación de sistema eléctrico general, incluyendo luces, indicadores del tablero, alternador y arranque.'
      ),
      crear_item(iconos.disc, 'Verificar tensión y eficacia del freno de mano y del pedal.'),
      crear_item(
        iconos.neuma,
        'Revisar presión de inflado de neumáticos y desgaste de la banda de rodamiento (se debe realizar en todas las revisiones).'
      ),
    ],
  },

  {
    km: '20.000',
    bg_image: '',
    col_izq: [
      crear_item(
        iconos.aceite,
        'Cambio de aceite de motor, filtro de aceite y filtro de aire. La periodicidad del cambio de aceite depende de las recomendaciones del fabricante.'
      ),
      crear_item(iconos.gota, 'Completar nivel de líquidos: agua de batería, diferencial, hidráulico, etc.'),
      crear_item(
        iconos.alibal,
        'Alineación y balanceo. Se debe realizar cada 10.000 kilómetros o si se ha pasado por un bache o tenido algún choque o topón.'
      ),
      crear_item(iconos.rot, 'Es conveniente, también, realizar rotación de neumáticos.'),
      crear_item(
        iconos.fren,
        'Realizar limpieza y mantenimiento del sistema de frenos, inspeccionar el desgaste de las pastillas, observando si es necesario sustituirlas.'
      ),
    ],
    col_der: [
      crear_item(
        iconos.bat,
        'Verificación de sistema eléctrico general, incluyendo luces, indicadores del tablero, alternador y arranque.'
      ),
      crear_item(iconos.disc, 'Verificar tensión y eficacia del freno de mano y del pedal.'),
      crear_item(
        iconos.neuma,
        'Revisar presión de inflado de neumáticos y desgaste de la banda de rodamiento (se debe realizar en todas las revisiones).'
      ),
      crear_item(iconos.filt, 'Cambiar filtro de aire del habitáculo o anti polen del aire acondicionado.'),
    ],
  },

  {
    km: '30.000',
    bg_image: fondo_gris,
    col_izq: [
      crear_item(
        iconos.aceite,
        'Cambio de aceite de motor, filtro de aceite y filtro de aire. La periodicidad del cambio de aceite depende de las recomendaciones del fabricante.'
      ),
      crear_item(iconos.gota, 'Completar nivel de líquidos: agua de batería, diferencial, hidráulico, etc.'),
      crear_item(
        iconos.alibal,
        'Alineación y balanceo. Se debe realizar cada 10.000 kilómetros o si se ha pasado por un bache o tenido algún choque o topón.'
      ),
      crear_item(iconos.rot, 'Es conveniente, también, realizar rotación de neumáticos.'),
      crear_item(
        iconos.bat,
        'Verificación de sistema eléctrico general, incluyendo luces, indicadores del tablero, alternador y arranque.'
      ),
      crear_item(
        iconos.neuma,
        'Revisar presión de inflado de neumáticos y desgaste de la banda de rodamiento (se debe realizar en todas las revisiones).'
      ),
    ],
    col_der: [
      crear_item(
        iconos.elev,
        'Elevar el vehículo y revisar fugas, estado de las articulaciones, rótulas y guardapolvos, tuberías y mangueras del vehículo.'
      ),
      crear_item(
        iconos.fren,
        'Cambio de pastillas de frenos delanteros y traseros, cambio de bandas dependiendo manual del fabricante.'
      ),
      crear_item(iconos.acei, 'Inspección aceite hidráulico (aceite diferencial en vehículos 4x4).'),
      crear_item(iconos.filt, 'Revisión del sistema del aire acondicionado.'),
      crear_item(
        iconos.engra,
        'Engrase de rodamientos en los vehículos que tienen rodamientos abiertos. Los que tienen rodamientos sellados deben cambiarse cuando presente ruido anormal.'
      ),
      crear_item(iconos.disc, 'Verificar tensión y eficacia del freno de mano y del pedal.'),
    ],
  },

  {
    km: '40.000',
    bg_image: '',
    col_izq: [
      crear_item(
        iconos.aceite,
        'Cambio de aceite de motor, filtro de aceite y filtro de aire. La periodicidad del cambio de aceite depende de las recomendaciones del fabricante.'
      ),
      crear_item(iconos.gota, 'Completar nivel de líquidos: agua de batería, diferencial, hidráulico, etc.'),
      crear_item(
        iconos.alibal,
        'Alineación y balanceo. Se debe realizar cada 10.000 kilómetros o si se ha pasado por un bache o tenido algún choque o topón.'
      ),
      crear_item(iconos.rot, 'Es conveniente, también, realizar rotación de neumáticos.'),
      crear_item(iconos.acei, 'Inspección aceite hidráulico (aceite diferencial en vehículos 4x4).'),
      crear_item(iconos.filt, 'Revisión del sistema del aire acondicionado.'),
      crear_item(
        iconos.engra,
        'Engrase de rodamientos en los vehículos que tienen rodamientos abiertos. Los que tienen rodamientos sellados deben cambiarse cuando presente ruido anormal.'
      ),
      crear_item(
        iconos.palanca,
        'En algunos modelos y, cuando lo recomiende el fabricante, realizar sustitución del aceite de la transmisión.'
      ),
      crear_item(
        iconos.radiador,
        'Realizar mantenimiento, limpieza y sondeo del radiador (no se recomienda cambiar refrigerantes).'
      ),
    ],
    col_der: [
      crear_item(
        iconos.bat,
        'Verificación de sistema eléctrico general, incluyendo luces, indicadores del tablero, alternador y arranque.'
      ),
      crear_item(iconos.disc, 'Verificar tensión y eficacia del freno de mano y del pedal.'),
      crear_item(
        iconos.elev,
        'Elevar el vehículo y revisar fugas, estado de las articulaciones, rótulas y guardapolvos, tuberías y mangueras del vehículo.'
      ),
      crear_item(
        iconos.fren,
        'Cambio de pastillas de frenos delanteros y traseros, cambio de bandas dependiendo manual del fabricante.'
      ),
      crear_item(
        iconos.neuma,
        'Revisar presión de inflado de neumáticos y desgaste de la banda de rodamiento (se debe realizar en todas las revisiones).'
      ),
      crear_item(
        iconos.check,
        'Realizar sincronización del motor, ya sea de carburador o inyección (cambio de bujías, instalación de alta, filtro de aire y de combustible).'
      ),
      crear_item(iconos.filt, 'Cambiar filtro de aire del habitáculo o anti polen del aire acondicionado.'),
      crear_item(iconos.amorti, 'Ajustar la suspensión.'),
    ],
  },
];

/* =========================
 * ✅ NUEVO: bloque 50.000 (layout distinto)
 * (NO altera bloques_kilometraje)
 * ========================= */
const bloque_50000 = Object.freeze({
  km: '50.000',
  bg_image: assets_50000.fondo,

  titulo: '50.000 Kilómetros',
  subtitulo: 'Repetir rutina de los 40.000 kms.',

  parrafo_1: 'Cada fabricante tiene sus propias especificaciones y, tal como explicamos inicialmente, éstas son recomendaciones generales. Esperamos que esta información te haya sido de utilidad.',
  parrafo_2: 'No olvides acudir a cualquiera de nuestros locales y pedir tu revisión de seguridad gratis.',

  icon_src: assets_50000.icono_repetir,
  icon_alt: 'Ícono repetir rutina',

  img_src: assets_50000.imagen,
  img_alt: 'Mecánico revisando frenos',
});

/* =========================
 * Helpers de UI
 * ========================= */
const estilo_fondo = (bloque) => {
  if (!bloque?.bg_image) return undefined;
  return { backgroundImage: `url(${bloque.bg_image})` };
};

const on_icon_error = (event) => {
  // Si el asset no existe, ocultamos la imagen (evita “imagen rota”).
  const el = event?.target;
  if (el && el.style) el.style.display = 'none';
};
</script>
